import Link from 'next/link';

/**
 * Site footer.
 *
 * Every public route must be reachable from here or from the navbar. Before
 * UXW-1 the footer carried four links and seven finished public pages
 * (/about, /blog, /insights, /releases, /security, /status, /support) had no
 * inbound link from anywhere in the app.
 *
 * When you add a public page, add it to a column below.
 */

type FooterLink = {
  label: string;
  href: string;
  external?: boolean;
};

const COLUMNS: ReadonlyArray<{ heading: string; links: readonly FooterLink[] }> = [
  {
    heading: 'Product',
    links: [
      { label: 'How it works', href: '/how-it-works' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Docs', href: '/docs' },
      { label: 'API reference', href: '/docs/api' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Blog', href: '/blog' },
      { label: 'Insights', href: '/insights' },
      { label: 'Release notes', href: '/releases' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Support', href: '/support' },
      { label: 'Security', href: '/security' },
      { label: 'System status', href: '/status' },
      { label: 'GitHub', href: 'https://github.com/topofthehill/verbal', external: true },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '/terms' },
    ],
  },
];

const linkClass =
  'font-sans text-sm text-graphite transition-colors hover:text-salmon focus-visible:text-salmon';

export default function Footer() {
  return (
    <footer className="border-t border-rule bg-paper">
      <div className="mx-auto max-w-[1100px] px-6 py-12 md:py-14">
        <nav aria-label="Footer">
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4">
            {COLUMNS.map((column) => (
              <div key={column.heading}>
                <h2 className="mb-4 font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink">
                  {column.heading}
                </h2>
                <ul className="flex flex-col gap-2.5">
                  {column.links.map((link) => (
                    <li key={link.href}>
                      {link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={linkClass}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <Link href={link.href} className={linkClass}>
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <div className="mt-12 flex flex-col items-start gap-2 border-t border-rule pt-6 md:flex-row md:items-center md:justify-between">
          <p className="font-sans text-sm text-graphite">&copy; 2026 Verbal</p>
          <p className="font-sans text-sm text-graphite">Track what your AI actually costs.</p>
        </div>
      </div>
    </footer>
  );
}
